import './index.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout';
import { LoginPage, RegisterPage } from './pages/Auth';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Properties from './pages/Properties';
import { TenancyChecker, LicensingChecker, Reminders } from './pages/Tools';
import Billing from './pages/Billing';
import RRA from './pages/RRA';
import Profile from './pages/Profile';
import Landing from './pages/Landing';
import { PrivacyPolicy, TermsAndConditions } from './pages/Legal';
import HMO from './pages/HMO';
import Deposits from './pages/Deposits';
import DocumentServing from './pages/DocumentServing';
import Notes from './pages/Notes';
import Tasks from './pages/Tasks';
import RentTracker from './pages/RentTracker';
import Calendar from './pages/Calendar';
import Contractors from './pages/Contractors';
import ContractorSignup from './pages/ContractorSignup';
import ContractorDashboard from './pages/ContractorDashboard';
import BuyReport from './pages/BuyReport';
import ViewReport from './pages/ViewReport';
import ComplianceReport from './pages/ComplianceReport';
import AccessGate from './components/AccessGate';
import Contact from './pages/Contact';
import { usePageTracking } from './hooks/useAnalytics';
import BillingBlocker from './components/BillingBlocker';
import BillingSuccess from './pages/BillingSuccess';
import Seo from './components/Seo';
import GuidesIndex from './pages/GuidesIndex';
import Guide from './pages/Guide';

function PrivateRoute({ children }) {
  const { user, billingReady, billingLoaded, billing } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  // Wait for billing status to load before deciding
  if (!billingLoaded) return null;

  // User has no Stripe subscription — show blocker
  // BillingBlocker handles the Stripe redirect
  if (!billingReady) {
    const plan = billing?.plan || 'starter';
    return <BillingBlocker plan={plan} autoRedirect />;
  }

  return children;
}

// Only these public marketing pages may appear in search results.
// Everything else (login, signup, reports, contractor pages, the app) gets noindex.
const INDEXABLE = ['/', '/privacy', '/terms', '/contact', '/guides'];
const isIndexable = (path) => {
  const p = path.replace(/\/+$/, '') || '/';
  return INDEXABLE.includes(p) || p.startsWith('/guides/'); // unknown slugs set their own noindex
};

function AppRoutes() {
  usePageTracking();
  const { pathname } = useLocation();
  const noindex = !isIndexable(pathname);
  return (
    <>
    {noindex && <Seo path={pathname} noindex />}
    <Routes>
      <Route path="/"             element={<Landing />} />
      <Route path="/privacy"      element={<PrivacyPolicy />} />
      <Route path="/terms"        element={<TermsAndConditions />} />
      <Route path="/login"        element={<LoginPage />} />
      <Route path="/register"     element={<RegisterPage />} />
      <Route path="/signup"       element={<Signup />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/report/:id"       element={<ComplianceReport />} />
      <Route path="/contractor/signup"    element={<ContractorSignup />} />
      <Route path="/contractor/login"     element={<ContractorSignup />} />
      <Route path="/contractor/success"   element={<ContractorDashboard />} />
      <Route path="/contractor/dashboard" element={<ContractorDashboard />} />
      <Route path="/report/buy"           element={<BuyReport />} />
      <Route path="/report/view/:token"   element={<ViewReport />} />
      <Route path="/contact"              element={<Contact />} />
      <Route path="/guides"               element={<GuidesIndex />} />
      <Route path="/guides/:slug"         element={<Guide />} />
      <Route path="/*" element={
        <PrivateRoute>
          <Layout>
            <Routes>
              <Route path="/dashboard"       element={<Dashboard />} />
              <Route path="/properties"      element={<Properties />} />
              <Route path="/tenancy-checker" element={<TenancyChecker />} />
              <Route path="/licensing"       element={<LicensingChecker />} />
              <Route path="/reminders"       element={<Reminders />} />
              <Route path="/billing"         element={<Billing />} />
              <Route path="/rra"             element={<RRA />} />
              <Route path="/hmo"             element={<HMO />} />
              <Route path="/deposits"         element={<Deposits />} />
              <Route path="/documents"         element={<DocumentServing />} />
              <Route path="/notes"             element={<Notes />} />
              <Route path="/tasks"             element={<Tasks />} />
              <Route path="/rent"              element={<RentTracker />} />
              <Route path="/calendar"          element={<Calendar />} />
              <Route path="/contractors"       element={<Contractors />} />
              <Route path="/profile"         element={<Profile />} />
              <Route path="/contact"         element={<Contact />} />
            </Routes>
          </Layout>
        </PrivateRoute>
      } />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <AccessGate>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </AccessGate>
  );
}